import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { orderService } from '../services/orderService';
import { planService } from '../services/planService';
import PlanManagement from '../components/PlanManagement';
import { 
  ShieldAlert, LogIn, LogOut, RefreshCw, ShoppingBag, Package, 
  Truck, CheckCircle2, XCircle, Phone, Loader2, ArrowRight
} from 'lucide-react';
import { Copy, Check } from 'lucide-react';
import PopupModal from '../components/PopupModal';

const STATUS_CONFIG = {
  Pending: { label: "Pending", className: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  Processing: { label: "Processing", className: "bg-purple-100 text-purple-800 border-purple-200" },
  Shipped: { label: "Out for Delivery", className: "bg-blue-100 text-blue-800 border-blue-200" },
  Delivered: { label: "Delivered", className: "bg-green-100 text-green-800 border-green-200" },
  Cancelled: { label: "Cancelled", className: "bg-red-100 text-red-800 border-red-200" },
};

export default function AdminDashboard() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [activeTab, setActiveTab] = useState('orders'); // 'orders' | 'plans'
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [popup, setPopup] = useState(null);
  const [copiedAddressId, setCopiedAddressId] = useState(null);

  useEffect(() => {
    checkAdmin();
  }, []);

  async function checkAdmin() {
    const admin = await authService.isAdmin();
    setIsAdmin(admin);
    if (admin) loadOrders();
  }

  async function loadOrders() {
    setLoadingOrders(true);
    try {
      const data = await orderService.getAllOrdersAdmin();
      setOrders(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOrders(false);
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setAuthError('');
    try {
      await authService.signIn(email, password);
      const admin = await authService.isAdmin();
      if (!admin) {
        setAuthError('Unauthorized: You do not have admin rights.');
        setIsLoggingIn(false);
        return;
      }
      setIsAdmin(true);
      loadOrders();
    } catch (err) {
      setAuthError(err.message || 'Login failed.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    await authService.signOut();
    setIsAdmin(false);
    setOrders([]);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await orderService.updateOrderStatus(orderId, newStatus);
      await loadOrders();
      setPopup({ title: "Status Updated", message: `Order #${orderId} changed to ${newStatus}` });
    } catch (err) {
      setPopup({ title: "Update Failed", message: err.message });
    } finally {
      setUpdatingId(null);
    }
  };

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'Pending').length,
    shipped: orders.filter(o => o.status === 'Shipped').length,
    delivered: orders.filter(o => o.status === 'Delivered').length,
    canceled: orders.filter(o => o.status === 'Cancelled').length,
  };

  const formatOrderDate = (createdAt) => {
    if (!createdAt) return 'Not available';
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(createdAt));
  };

  const formatRequirementTime = (order) => {
    if (!order.preferred_delivery_date && !order.preferred_delivery_time) return 'Not specified';
    const date = order.preferred_delivery_date
      ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(`${order.preferred_delivery_date}T00:00:00`))
      : 'Date not specified';
    return `${date}${order.preferred_delivery_time ? `, ${order.preferred_delivery_time}` : ''}`;
  };

  const copyAddress = async (order) => {
    const fullAddress = [order.address, order.city, order.state, order.pincode].filter(Boolean).join(', ');
    await navigator.clipboard.writeText(fullAddress);
    setCopiedAddressId(order.id);
    window.setTimeout(() => setCopiedAddressId(null), 1600);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-xl max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-50 text-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert size={32} />
            </div>
            <h2 className="font-extrabold text-2xl text-slate-900 mb-1">Admin Login</h2>
            <p className="text-slate-500 text-xs">Enter your Supabase admin credentials to access the dashboard.</p>
          </div>

          {authError && <div className="mb-4 p-3 bg-red-50 text-red-600 text-xs rounded-xl font-medium border border-red-200">{authError}</div>}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Username / Email</label>
              <input
                type="email"
                required
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Password</label>
              <input
                type="password"
                required
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
            </div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full py-3.5 bg-blue-900 hover:bg-blue-950 text-white font-bold text-sm rounded-xl transition-all shadow-md flex items-center justify-center gap-2"
            >
              {isLoggingIn ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />} Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-200">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-500 text-xs sm:text-sm">Manage all A1 Sims orders and operator plans</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">Admin Active</span>
          <button onClick={loadOrders} className="p-2 rounded-xl border bg-white hover:bg-slate-50 text-slate-700">
            <RefreshCw size={15} />
          </button>
          <button onClick={handleLogout} className="px-4 py-2 rounded-xl bg-red-50 text-red-600 font-bold text-xs hover:bg-red-100 flex items-center gap-1.5">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      {/* Stats Counter */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><ShoppingBag size={18} /></div>
          <div><div className="text-xl font-bold text-slate-900">{stats.total}</div><div className="text-[11px] text-slate-400">Total</div></div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Package size={18} /></div>
          <div><div className="text-xl font-bold text-amber-700">{stats.pending}</div><div className="text-[11px] text-slate-400">Pending</div></div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><Truck size={18} /></div>
          <div><div className="text-xl font-bold text-blue-700">{stats.shipped}</div><div className="text-[11px] text-slate-400">Delivery</div></div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><CheckCircle2 size={18} /></div>
          <div><div className="text-xl font-bold text-emerald-700">{stats.delivered}</div><div className="text-[11px] text-slate-400">Delivered</div></div>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0"><XCircle size={18} /></div>
          <div><div className="text-xl font-bold text-rose-700">{stats.canceled}</div><div className="text-[11px] text-slate-400">Cancelled</div></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all ${
            activeTab === 'orders' ? 'bg-blue-900 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200'
          }`}
        >
          📦 Orders Queue ({orders.length})
        </button>
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all ${
            activeTab === 'plans' ? 'bg-blue-900 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200'
          }`}
        >
          📋 Plan Management
        </button>
      </div>

      {/* ORDERS TAB */}
      {activeTab === 'orders' && (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-[1320px] w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-bold border-b border-slate-200">
                <tr>
                  <th className="w-[150px] p-4 align-top">Order ID</th>
                  <th className="w-[125px] p-4 align-top">Type</th>
                  <th className="w-[150px] p-4 align-top">Customer</th>
                  <th className="w-[150px] p-4 align-top">Phone</th>
                  <th className="w-[210px] p-4 align-top">Email</th>
                  <th className="w-[210px] p-4 align-top">Selected Plan</th>
                  <th className="w-[250px] p-4 align-top">Delivery Address</th>
                  <th className="w-[180px] p-4 align-top">Order generation time</th>
                  <th className="w-[200px] p-4 align-top">SIM requirement time</th>
                  <th className="w-[130px] p-4 align-top">Status</th>
                  <th className="w-[170px] p-4 align-top">Change Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((o) => {
                  const cfg = STATUS_CONFIG[o.status] || STATUS_CONFIG.Pending;
                  return (
                    <tr key={o.id} className="hover:bg-slate-50/80 align-top">
                      <td className="p-4 font-mono font-bold text-blue-900 break-all">{o.order_number}</td>
                      <td className="p-4"><span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">{o.order_type}</span></td>
                      <td className="p-4 font-bold text-slate-900 break-words">{o.customer_name}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-600 font-medium whitespace-nowrap">{o.phone}</span>
                          <a href={`tel:${o.phone}`} className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center hover:bg-emerald-200">
                            <Phone size={12} />
                          </a>
                        </div>
                      </td>
                      <td className="p-4 text-xs text-slate-600 break-all">{o.email || 'Not provided'}</td>
                      <td className="p-4 text-xs text-slate-800">
                        <div className="font-bold">{o.operator} - {o.plan_name}</div>
                        <div className="mt-1 text-slate-500">Plan ID: {o.plan_id}</div>
                        <div className="font-bold text-orange-600">₹{o.plan_price}</div>
                      </td>
                      <td className="p-4 text-xs text-slate-500 break-words">
                        <div className="font-medium text-slate-700">{o.address}</div>
                        <div>{o.city}, {o.state}</div>
                        <div>Pincode: {o.pincode}</div>
                        <button
                          type="button"
                          onClick={() => copyAddress(o)}
                          className="mt-2 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-blue-700 transition hover:bg-blue-50"
                        >
                          {copiedAddressId === o.id ? <Check size={12} /> : <Copy size={12} />}
                          {copiedAddressId === o.id ? 'Copied' : 'Copy'}
                        </button>
                      </td>
                      <td className="p-4 text-xs font-medium text-slate-600 whitespace-nowrap">{formatOrderDate(o.created_at)}</td>
                      <td className="p-4 text-xs font-medium text-slate-600">{formatRequirementTime(o)}</td>
                      <td className="p-4"><span className={`px-3 py-1 rounded-full text-xs font-bold border ${cfg.className}`}>{cfg.label}</span></td>
                      <td className="p-4">
                        <select
                          value={o.status}
                          disabled={updatingId === o.id}
                          onChange={(e) => handleStatusChange(o.id, e.target.value)}
                          className="text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200 bg-white"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Processing">Processing</option>
                          <option value="Shipped">Out for Delivery</option>
                          <option value="Delivered">Delivered</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PLANS TAB */}
      {activeTab === 'plans' && (
        <PlanManagement />
      )}

      <PopupModal
        isOpen={!!popup}
        title={popup?.title || ""}
        message={popup?.message || ""}
        onClose={() => setPopup(null)}
      />
    </div>
  );
}